<script setup>
// §8.5 Select — the NATIVE <select> element. Fully accessible and keyboard-
// operable with zero JavaScript; the platform renders the option list. We only
// restyle the control chrome and supply a custom chevron (native option popups
// can't be themed, and that's an acceptable trade for no dependency + no JS).
// Options: [{ value, label }].
import LField from './Field.vue'
import LIcon from '../base/Icon.vue'

defineProps({
	label: { type: String, default: null },
	hint: { type: String, default: null },
	options: { type: Array, default: () => [] },
	placeholder: { type: String, default: 'Select…' },
	disabled: { type: Boolean, default: false },
	// The accessible name when there is no visible label — e.g. a select inside a
	// table cell, where the column header carries the meaning visually.
	ariaLabel: { type: String, default: null },
})
const model = defineModel({ default: null })
</script>

<template>
	<LField :label="label" :hint="hint">
		<template #label-action><slot name="label-action" /></template>
		<template #default="{ id }">
			<div class="select-wrap">
				<select :id="id" v-model="model" :disabled="disabled" :aria-label="ariaLabel" class="native-select"
					:class="{ empty: model === null || model === '' }">
					<option v-if="placeholder" :value="null" disabled>{{ placeholder }}</option>
					<option v-for="o in options" :key="o.value" :value="o.value">{{ o.label }}</option>
				</select>
				<LIcon name="chevron-down" :size="15" class="chev" />
			</div>
		</template>
	</LField>
</template>

<style scoped>
.select-wrap
{
	position: relative;
	display: flex;
	align-items: center;
}

.native-select
{
	appearance: none;
	-webkit-appearance: none;
	width: 100%;
	font-family: var(--font-ui);
	font-size: var(--text-base);
	color: var(--text);
	padding: 9px 36px 9px 12px;
	border: 1px solid var(--line);
	border-radius: var(--r);
	background: var(--surface);
	min-height: var(--target-min);
	cursor: pointer;
}

.native-select.empty
{
	color: var(--text-3);
}

.native-select:hover
{
	border-color: var(--line-strong);
}

.native-select:focus-visible
{
	border-color: var(--interactive);
}

.native-select:disabled
{
	background: var(--surface-sunken);
	color: var(--text-3);
	cursor: not-allowed;
}

.chev
{
	position: absolute;
	right: 12px;
	color: var(--icon);
	pointer-events: none;
}
</style>
